/**
 * useIncomeExpensesReport Hook
 *
 * A React hook for fetching income vs expenses report data from the API.
 * Provides period-by-period comparison of income and expenses for bar chart visualization.
 *
 * @see docs/plans/iterations/transaction-reporting/contracts/frontend-rails-api.json
 */

import { useState, useCallback } from 'react';
import type { IncomeExpensesReportResponse, ReportGranularity, Owner } from '@/types/accounting';

/**
 * Return type for the useIncomeExpensesReport hook
 */
export interface UseIncomeExpensesReportReturn {
  /** Current income vs expenses report data */
  data: IncomeExpensesReportResponse | null;
  /** Whether an API request is in progress */
  loading: boolean;
  /** Error message from the last failed request */
  error: string | null;
  /** Fetch income vs expenses report with current parameters */
  fetchReport: () => Promise<IncomeExpensesReportResponse>;
  /** Clear any error state */
  clearError: () => void;
}

/**
 * Hook for fetching income vs expenses report
 *
 * @param startDate - Start date of the report period (ISO date string: "2025-01-01")
 * @param endDate - End date of the report period (ISO date string: "2025-12-31")
 * @param granularity - Time granularity for aggregation ("monthly", "quarterly", or "yearly")
 * @param owner - Optional owner filter ("family" or "estate")
 * @returns Object containing data, loading state, error, and fetch function
 *
 * @example
 * ```typescript
 * const { data, loading, error, fetchReport } = useIncomeExpensesReport(
 *   '2025-01-01',
 *   '2025-12-31',
 *   'monthly',
 *   'family'
 * );
 *
 * // Fetch report on mount
 * useEffect(() => {
 *   fetchReport();
 * }, [fetchReport]);
 *
 * // Display period data
 * if (data) {
 *   data.periods.forEach(period => {
 *     console.log(`${period.period}: Income ${period.incomeCents}, Expenses ${period.expensesCents}`);
 *   });
 * }
 * ```
 */
export function useIncomeExpensesReport(
  startDate: string,
  endDate: string,
  granularity: ReportGranularity,
  owner?: Owner
): UseIncomeExpensesReportReturn {
  const [data, setData] = useState<IncomeExpensesReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch income vs expenses report from the API
   */
  const fetchReport = useCallback(async (): Promise<IncomeExpensesReportResponse> => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        granularity: granularity,
      });
      if (owner) {
        params.set('owner', owner);
      }

      const response = await fetch(`/api/accounting/reports/income_vs_expenses?${params}`);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to fetch report: ${response.statusText}`);
      }

      const json = (await response.json()) as IncomeExpensesReportResponse;
      setData(json);
      return json;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load income vs expenses report';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, granularity, owner]);

  return { data, loading, error, fetchReport, clearError };
}
